import { useSearchParams } from 'react-router-dom';

/**
 * @typedef {Object} QueryParamsHook
 * @property {(key: string, value: string) => void} setQueryParam - Sets a query parameter in the URL
 * @property {(key: string) => void} removeQueryParam - Removes a query parameter from the URL
 * @property {() => Object.<string, string>} getParams - Gets all query parameters as a key-value object
 * @property {() => string} getQueryString - Gets the complete query string from the URL
 * @property {() => void} clearParams - Removes all query parameters from the URL
 * @property {(key: string) => string} getParam - Gets a query parameter from the URL
 */

/**
 * Custom hook for managing URL query parameters in React applications.
 * Provides utility functions to manipulate and retrieve URL search parameters.
 *
 * @returns {QueryParamsHook} An object containing methods to manage query parameters
 */
export default function useQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Sets a query parameter in the URL
   * @param {string} key - The key of the query parameter
   * @param {string} value - The value to set for the query parameter
   */
  function setQueryParam(key, value) {
    const newParams = new URLSearchParams(window.location.search);
    newParams.set(key, value);
    setSearchParams(newParams);
  }

  /**
   * Sets multiple query parameters in the URL
   * @param {Object.<string, string>} params - An object containing key-value pairs of query parameters
   */
  function setBulkQueryParams(params) {
    const newParams = new URLSearchParams(window.location.search);

    Object.entries(params).forEach(([key, value]) => {
      newParams.set(key, value);
    });

    setSearchParams(newParams);
  }

  /**
   * Removes a query parameter from the URL
   * @param {string} key - The key of the query parameter to remove
   */
  function removeQueryParam(key) {
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete(key);
    setSearchParams(newParams);
  }

  /**
   * Gets all query parameters as a key-value object
   * @returns {Object.<string, string>} An object containing all query parameters
   */
  function getParams() {
    return Object.fromEntries(searchParams.entries());
  }

  /**
   * Gets a query parameter from the URL
   * @param {string} key - The key of the query parameter to get
   * @returns {string} The value of the query parameter
   */
  function getParam(key) {
    return searchParams.get(key) || '';
  }

  /**
   * Gets the complete query string from the URL
   * @returns {string} The query string without the leading '?'
   */
  function getQueryString() {
    return searchParams.toString();
  }

  /**
   * Removes all query parameters from the URL
   */
  function clearParams() {
    setSearchParams(new URLSearchParams());
  }

  return {
    setQueryParam,
    removeQueryParam,
    getParams,
    getQueryString,
    clearParams,
    getParam,
    setBulkQueryParams
  };
}
